import type { QuoteDirection } from "../../features/quotes/quoteDirectionDefaulting.js";
import type {
  ProjectQuote_IncomingQuoteFragment,
  ProjectQuote_OutgoingQuoteFragment,
} from "./ProjectQuotes.generated.js";

export const PROJECT_QUOTE_QUICK_FILTER_KEYS = [
  "DRAFT",
  "BINDING_QUOTE",
  "RECEIVED",
  "ACCEPTED",
  "DECLINED",
] as const;

export type ProjectQuoteQuickFilterKey =
  (typeof PROJECT_QUOTE_QUICK_FILTER_KEYS)[number];

export type ProjectQuoteQuickFilterQuote = Pick<
  ProjectQuote_OutgoingQuoteFragment | ProjectQuote_IncomingQuoteFragment,
  "isPublished" | "isBinding" | "agreement"
>;

interface ProjectQuoteQuickFilter {
  directions: readonly QuoteDirection[];
  matches: (quote: ProjectQuoteQuickFilterQuote) => boolean;
}

/**
 * Status quick filters of the project Quotes tab. Filtering runs on the
 * client, so each predicate compares the quote's single `agreement` value -
 * unlike Docs > Quotes, whose server-side filters take arrays.
 */
const PROJECT_QUOTE_QUICK_FILTERS: Record<
  ProjectQuoteQuickFilterKey,
  ProjectQuoteQuickFilter
> = {
  DRAFT: {
    directions: ["OUTGOING"],
    matches: quote => !quote.isPublished,
  },
  BINDING_QUOTE: {
    directions: ["OUTGOING"],
    matches: quote =>
      quote.isPublished && quote.isBinding && quote.agreement === "NONE",
  },
  RECEIVED: {
    directions: ["INCOMING"],
    matches: quote => quote.agreement === "NONE",
  },
  ACCEPTED: {
    directions: ["OUTGOING", "INCOMING"],
    matches: quote => quote.agreement === "YES",
  },
  DECLINED: {
    directions: ["OUTGOING", "INCOMING"],
    matches: quote => quote.agreement === "NO",
  },
};

export const isProjectQuoteQuickFilterKey = (
  key: string | undefined
): key is ProjectQuoteQuickFilterKey =>
  PROJECT_QUOTE_QUICK_FILTER_KEYS.some(filterKey => filterKey === key);

export const isProjectQuoteQuickFilterVisible = (
  key: ProjectQuoteQuickFilterKey,
  direction: QuoteDirection
): boolean => PROJECT_QUOTE_QUICK_FILTERS[key].directions.includes(direction);

/**
 * Whether a quote passes the given quick filter. No filter, or a key that
 * names no quick filter (e.g. a stale `quickfilter` URL param), matches every
 * quote.
 */
export const matchesProjectQuoteQuickFilter = (
  quote: ProjectQuoteQuickFilterQuote,
  key: string | undefined
): boolean =>
  isProjectQuoteQuickFilterKey(key)
    ? PROJECT_QUOTE_QUICK_FILTERS[key].matches(quote)
    : true;
